import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

const CATEGORIES = [
  { value: 'event',      label: 'Event',       color: '#006BFF' },
  { value: 'leave',      label: 'Leave',       color: '#dc2626' },
  { value: 'meeting',    label: 'Meeting',     color: '#7c3aed' },
  { value: 'conference', label: 'Conference',  color: '#0891b2' },
  { value: 'training',   label: 'Training',    color: '#16a34a' },
];

// Preset colour swatches (shown when no custom color is picked)
const COLOR_PRESETS = [
  '#006BFF', '#dc2626', '#7c3aed', '#0891b2',
  '#16a34a', '#f59e0b', '#db2777', '#4b5563',
];

const DEFAULT_STATE = {
  title:              '',
  notes:              '',
  category:           'event',
  startDate:          '',
  startTime:          '',
  endDate:            '',
  endTime:            '',
  blocksAvailability: true,
  color:              '',
  location:           '',
  attendeeIds:        [],
};

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Normalise any date value (JS Date, ISO string */
function toDateStr(v) {
  if (!v) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v).slice(0, 10);
}

/**
 * Modal for creating / editing / deleting an internal Team Calendar event.
 *
 * Props:
 *   open, onClose, onSaved, onDeleted
 *   event        {object|null}  — null = create; existing event = edit
 *   defaultStart {string|null}  — "YYYY-MM-DD" pre-fill for create
 *   managers     {Array}        — [{ manager_id, name }]
 *   currentManagerId {number}
 *   isPHAdmin    {boolean}
 *   onSaveRequest   {(payload, id?) => Promise}
 *   onDeleteRequest {(id) => Promise}
 */
export default function InternalEventModal({
  open,
  onClose,
  onSaved,
  onDeleted,
  event,
  defaultStart,
  managers = [],
  currentManagerId,
  isPHAdmin,
  onSaveRequest,
  onDeleteRequest,
}) {
  const isEdit = !!event;

  const [form, setForm]         = useState(DEFAULT_STATE);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState(null);

  // Populate / reset form when modal opens
  useEffect(() => {
    if (!open) return;
    setError(null);

    if (isEdit) {
      setForm({
        title:              event.title              || '',
        notes:              event.notes              || '',
        category:           event.category           || 'event',
        startDate:          toDateStr(event.start_date),
        startTime:          event.start_time ? String(event.start_time).slice(0, 5) : '',
        endDate:            toDateStr(event.end_date),
        endTime:            event.end_time   ? String(event.end_time).slice(0, 5)   : '',
        blocksAvailability: event.blocks_availability !== false,
        color:              event.color              || '',
        location:           event.location           || '',
        attendeeIds:        event.attendee_ids       || [],
      });
    } else {
      const today = defaultStart?.slice(0, 10) || todayStr();
      setForm({
        ...DEFAULT_STATE,
        startDate:   today,
        endDate:     today,
        attendeeIds: currentManagerId ? [currentManagerId] : [],
      });
    }
  }, [open, event, defaultStart, isEdit, currentManagerId]);

  const canEdit = isEdit
    ? (isPHAdmin || event?.created_by === currentManagerId)
    : true;

  const set = (field) => (e) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const setSwitch = (field) => (e) =>
    setForm(f => ({ ...f, [field]: e.target.checked }));

  const handleStartDateChange = (e) => {
    const newStart = e.target.value;
    setForm(f => {
      // While start and end are in sync (equal), keep them moving together.
      // Once the user sets end to a later date, stop auto-updating it.
      const syncEnd = !isEdit && f.endDate === f.startDate;
      return { ...f, startDate: newStart, ...(syncEnd ? { endDate: newStart } : {}) };
    });
  };

  const handleAttendeeChange = (_, value) =>
    setForm(f => ({ ...f, attendeeIds: value.map(m => Number(m.manager_id)) }));

  const handleColorPreset = (hex) =>
    setForm(f => ({ ...f, color: f.color === hex ? '' : hex }));

  const resolvedColor =
    form.color ||
    (CATEGORIES.find(c => c.value === form.category)?.color ?? '#4b5563');

  const handleSave = async () => {
    if (!form.title.trim())  { setError('Title is required.');            return; }
    if (!form.startDate)     { setError('Start date is required.');        return; }
    if (!form.endDate)       { setError('End date is required.');           return; }
    if (form.endDate < form.startDate) { setError('End date must be on or after start date.'); return; }

    setSaving(true); setError(null);
    try {
      await onSaveRequest({
        title:              form.title.trim(),
        notes:              form.notes.trim() || null,
        category:           form.category,
        startDate:          form.startDate,
        startTime:          form.startTime || null,
        endDate:            form.endDate,
        endTime:            form.endTime   || null,
        blocksAvailability: form.blocksAvailability,
        color:              form.color || null,
        location:           form.location.trim() || null,
        attendeeIds:        form.attendeeIds,
      }, isEdit ? event.id : null);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save event.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!event?.id) return;
    setDeleting(true); setError(null);
    try {
      await onDeleteRequest(event.id);
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to delete event.');
    } finally { setDeleting(false); }
  };

  const selectedAttendees = managers.filter(m =>
    form.attendeeIds.map(Number).includes(Number(m.manager_id))
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}>

      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: resolvedColor, flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
            {isEdit ? 'Edit Event' : 'New Event'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}
          sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Body ── */}
      <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Title */}
        <TextField label="Title" value={form.title} onChange={set('title')}
          fullWidth required disabled={!canEdit} inputProps={{ maxLength: 255 }} autoFocus={!isEdit} />

        {/* Category */}
        <FormControl fullWidth disabled={!canEdit}>
          <InputLabel>Category</InputLabel>
          <Select value={form.category} label="Category" onChange={set('category')}>
            {CATEGORIES.map(c => (
              <MenuItem key={c.value} value={c.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color }} />
                  {c.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Dates + optional times */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Start date" type="date"
            value={form.startDate} onChange={handleStartDateChange}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flex: '1 1 140px' }} required disabled={!canEdit}
          />
          <TextField
            label="Start time (optional)" type="time"
            value={form.startTime} onChange={set('startTime')}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flex: '0 0 150px' }} disabled={!canEdit}
          />
          <TextField
            label="End date" type="date"
            value={form.endDate} onChange={set('endDate')}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput:  { min: form.startDate || undefined },
            }}
            sx={{ flex: '1 1 140px' }} disabled={!canEdit}
          />
          <TextField
            label="End time (optional)" type="time"
            value={form.endTime} onChange={set('endTime')}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flex: '0 0 150px' }} disabled={!canEdit}
          />
        </Box>

        {/* Attendees */}
        <Autocomplete
          multiple
          options={managers}
          getOptionLabel={m => m.name || ''}
          isOptionEqualToValue={(o, v) => Number(o.manager_id) === Number(v.manager_id)}
          value={selectedAttendees}
          onChange={handleAttendeeChange}
          disabled={!canEdit}
          renderInput={params =>
            <TextField {...params} label="Attendees" placeholder="Add team member…" />
          }
          renderTags={(value, getTagProps) =>
            value.map((m, i) => (
              <Box key={m.manager_id} component="span"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  bgcolor: '#ede9fe', color: '#5b21b6', borderRadius: 2,
                  px: 1, py: 0.25, fontSize: '0.78rem', fontWeight: 600, mr: 0.5, mb: 0.25,
                }}>
                {m.name}
                {canEdit && (
                  <span onClick={getTagProps({ index: i }).onDelete}
                    style={{ cursor: 'pointer', marginLeft: 4, fontSize: '0.75rem', opacity: 0.7 }}>
                    ✕
                  </span>
                )}
              </Box>
            ))
          }
        />

        {/* Location */}
        <TextField
          label="Location (optional)"
          value={form.location}
          onChange={set('location')}
          fullWidth
          disabled={!canEdit}
          inputProps={{ maxLength: 255 }}
          InputProps={{
            startAdornment: (
              <PlaceOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', mr: 0.5 }} />
            ),
          }}
        />

        {/* Notes */}
        <TextField label="Notes (optional)" value={form.notes} onChange={set('notes')}
          fullWidth multiline minRows={2} disabled={!canEdit} inputProps={{ maxLength: 2000 }} />

        {/* Colour override + blocks availability */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Colour swatches */}
          <Box>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', mb: 0.75 }}>
              Colour (optional)
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {COLOR_PRESETS.map(hex => (
                <Tooltip key={hex} title={hex}>
                  <Box onClick={() => canEdit && handleColorPreset(hex)} sx={{
                    width: 22, height: 22, borderRadius: '50%', bgcolor: hex,
                    cursor: canEdit ? 'pointer' : 'default',
                    border: form.color === hex ? '2.5px solid #111' : '2px solid transparent',
                    boxSizing: 'border-box',
                    transition: 'transform 0.1s',
                    '&:hover': canEdit ? { transform: 'scale(1.2)' } : {},
                  }} />
                </Tooltip>
              ))}
              {/* Native colour picker fallback */}
              {canEdit && (
                <Tooltip title="Custom colour">
                  <Box component="label" sx={{
                    width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                    border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '0.75rem', color: '#9ca3af',
                  }}>
                    +
                    <input type="color" value={form.color || '#000000'} onChange={set('color')}
                      style={{ opacity: 0, position: 'absolute', width: 0, height: 0 }} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Blocks availability */}
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="When on, team members assigned to this event will show as unavailable in Task Manager">
              <FormControlLabel
                control={
                  <Switch checked={form.blocksAvailability} onChange={setSwitch('blocksAvailability')}
                    disabled={!canEdit} size="small" />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BlockOutlinedIcon sx={{ fontSize: 15, color: '#9ca3af' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#374151' }}>
                      Blocks availability
                    </Typography>
                  </Box>
                }
                labelPlacement="start"
                sx={{ ml: 0, mr: 0 }}
              />
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Typography sx={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 600 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <Divider />

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        {isEdit && canEdit ? (
          <Button color="error"
            startIcon={deleting ? <CircularProgress size={16} color="error" /> : <DeleteOutlineIcon />}
            onClick={handleDelete} disabled={saving || deleting}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            Delete
          </Button>
        ) : <Box />}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={saving || deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          {canEdit && (
            <Button variant="contained" onClick={handleSave}
              disabled={saving || deleting || !form.title.trim()}
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
              sx={{
                textTransform: 'none', fontWeight: 700,
                bgcolor: resolvedColor,
                '&:hover': { bgcolor: resolvedColor, filter: 'brightness(0.9)' },
              }}>
              {isEdit ? 'Save changes' : 'Add to calendar'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
